import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Plus, Minus } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

export default function Expertise() {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="py-24 md:py-36 bg-[#0A0A0A] text-[#F7F7F9]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="flex items-center gap-3 mb-12"
        >
          <span className="w-8 h-px bg-[#F7F7F9]" />
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#F7F7F9]/70">
            {t.expertise.overline} / 02
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight font-medium leading-[1.02] max-w-[20ch] mb-12"
        >
          {t.expertise.title}
        </motion.h2>

        {/* Zones d'intervention */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease, delay: 0.15 }}
          className="mb-16 md:mb-24 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end border-y border-[#F7F7F9]/15 py-8"
        >
          <div className="md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F7F7F9]/60">
            Zones d'intervention
          </div>
          <div className="md:col-span-9 flex flex-wrap items-baseline gap-x-8 gap-y-3 font-display text-2xl md:text-3xl tracking-tight">
            <span className="text-[#F7F7F9]">France</span>
            <span className="text-[#F7F7F9]/30">·</span>
            <span className="text-[#F7F7F9]">Luxembourg</span>
            <span className="text-[#F7F7F9]/30">·</span>
            <span className="text-[#F7F7F9]">Algérie</span>
            <span className="ml-auto font-mono text-[10px] tracking-[0.22em] uppercase text-[#D35400]">
              Projets à distance possibles
            </span>
          </div>
        </motion.div>

        <div className="border-t border-[#F7F7F9]/15">
          {t.expertise.items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <motion.div
                key={item.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease, delay: i * 0.08 }}
                className="border-b border-[#F7F7F9]/15"
              >
                <button
                  data-testid={`expertise-item-${i}`}
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  className="w-full py-7 md:py-8 flex items-start gap-4 md:gap-10 text-left group"
                >
                  <span className="font-mono text-xs tracking-[0.2em] text-[#F7F7F9]/50 pt-2 w-10">
                    {item.n}
                  </span>
                  <span
                    className={`flex-1 font-display text-2xl md:text-4xl tracking-tight transition-colors ${
                      isOpen ? "text-[#D35400]" : "group-hover:text-[#F7F7F9]/80"
                    }`}
                  >
                    {item.t}
                  </span>
                  <span className="pt-2">
                    {isOpen ? (
                      <Minus size={22} className="text-[#D35400]" />
                    ) : (
                      <Plus size={22} className="text-[#F7F7F9]/60 group-hover:text-[#F7F7F9] transition-colors" />
                    )}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease }}
                      className="overflow-hidden"
                    >
                      <div className="pb-10 pl-14 md:pl-[74px] max-w-[60ch]">
                        <p className="text-base md:text-lg font-light leading-relaxed text-[#F7F7F9]/80">
                          {item.d}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
